import type { AdminMetrics } from '@kk/domain';
import { Card, SectionLabel, Text, Title2 } from '@kk/ui';

import { useContentCan } from '@/auth/SessionProvider';
import { Status } from '@/components/bits';
import { deck } from '@/copy/deck';
import { useErrorMessage, useMetrics } from '@/data/hooks';

const c = deck.metrics;

/**
 * **THE ADMIN'S METRICS — COUNTS ONLY** (Evan, 28 Sep). Owners only. Every figure is a count over many families; the
 * server sends nothing that names a family, a person or an id, so there is nothing here to click through to.
 */
export function MetricsScreen() {
  const owner = useContentCan('team');
  const metrics = useMetrics(owner);
  const errorMessage = useErrorMessage();

  if (!owner) return <Status testID="metrics-owners-only">{c.ownersOnly}</Status>;
  const m = metrics.data;
  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <Title2 tone="ink">{c.title}</Title2>
          <Text variant="bodySm" tone="muted">
            {c.subtitle}
          </Text>
        </div>
      </div>
      {metrics.error ? <Status testID="metrics-error">{errorMessage(metrics.error)}</Status> : null}
      {m ? <Figures m={m} /> : null}
    </>
  );
}

function Row({ label, value, testID }: { label: string; value: string | number; testID?: string }) {
  return (
    <div className="grid-row" data-testid={testID} style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Text variant="bodySm" tone="ink">
        {label}
      </Text>
      <Text variant="rowTitleSm" tone="ink">
        {String(value)}
      </Text>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card variant="quiet" radius="row" padding={18}>
      <div className="stack">
        <SectionLabel>{title}</SectionLabel>
        {children}
      </div>
    </Card>
  );
}

function Figures({ m }: { m: AdminMetrics }) {
  const newest = <T,>(xs: T[]) => [...xs].reverse();
  return (
    <div className="stack" data-testid="metrics">
      <Section title={c.firstApproved}>
        <Row
          label={c.firstApprovedOf}
          value={`${m.firstApprovedChore.families} / ${m.firstApprovedChore.ofFamilies}`}
          testID="metrics-first-approved"
        />
      </Section>
      <Section title={c.setup}>
        {m.setup.map((s) => (
          <Row key={s.step} label={c.steps[s.step] ?? s.step} value={s.accounts} testID={`metrics-setup-${s.step}`} />
        ))}
      </Section>
      <Section title={c.familyPlus}>
        <Row label={c.subscribedNow} value={m.familyPlus.subscribedNow} testID="metrics-subscribed-now" />
        <Row label={c.freeYearNow} value={m.familyPlus.freeYearNow} testID="metrics-free-year-now" />
        {newest(m.familyPlus.weeks).map((w) => (
          <Row
            key={w.week}
            label={c.weekOf(w.week)}
            value={c.startsAndCancellations(w.starts, w.cancellations)}
            testID={`metrics-plus-${w.week}`}
          />
        ))}
      </Section>
      <Section title={c.weeklyActive}>
        {newest(m.weeklyActiveFamilies).map((w) => (
          <Row key={w.week} label={c.weekOf(w.week)} value={w.families} testID={`metrics-active-${w.week}`} />
        ))}
      </Section>
      <Section title={c.signups}>
        {newest(m.signupsByDay).map((d) => (
          <Row key={d.day} label={d.day} value={d.families} testID={`metrics-signups-${d.day}`} />
        ))}
      </Section>
      <Text variant="meta" tone="muted">
        {c.asOf(m.asOf, m.timezone)}
      </Text>
    </div>
  );
}
